import { useEffect, useMemo, useState } from 'react';
import { siteDataApi } from '../api/siteData';
import useAppStore from '../store/useAppStore';
import { MultiSelect, type SelectOption } from './MultiSelect';
import Pagination from './Pagination';

const pageRequests = new Map<string, Promise<Record<string, { list: string[]; total: number }>>>();
const FRONTEND_PAGE_SIZE = 10;
const FRONTEND_MAX_PAGES = 100;
const FRONTEND_MAX_OPTIONS = FRONTEND_PAGE_SIZE * FRONTEND_MAX_PAGES;

const businessShop = (brand: string, siteCode: string) => {
  if (brand === 'vooglam') return 'Vooglam';
  const region = siteCode.toLowerCase().split('-')[0];
  return ({ us: 'Zeelool', fr: 'Zeelool_fr', de: 'Zeelool_de', ca: 'Zeelool_ca', uk: 'Zeelool_uk' } as Record<string, string>)[region] ?? '';
};

export function ReportLabelSelect({ dataset, field, pageFields, fallbackOptions = [], includeAll = false, value, onChange, width = 170, single = false, exclude = false, onExcludeChange, allowExcludeAll = false, paginated = false, pageSize = FRONTEND_PAGE_SIZE }: {
  dataset: string; field: string; pageFields?: readonly string[]; fallbackOptions?: readonly string[]; includeAll?: boolean; value: string[]; onChange: (value: string[]) => void; width?: number | string; single?: boolean; exclude?: boolean; onExcludeChange?: (exclude: boolean) => void; allowExcludeAll?: boolean; paginated?: boolean; pageSize?: number;
}) {
  const { brand, siteId, sites } = useAppStore();
  const site = useMemo(() => businessShop(brand, sites.find(item => item.id === siteId)?.code ?? ''), [brand, siteId, sites]);
  const [list, setList] = useState<string[]>([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const fieldSignature = (pageFields?.length ? pageFields : [field]).join(',');
  useEffect(() => { setPage(1); }, [dataset, field, search, site]);
  useEffect(() => {
    if (!site) { setList([]); setTotal(0); return; }
    let active = true;
    const timer = window.setTimeout(async () => {
      setLoading(true);
      try {
        if (paginated) {
          const res = await siteDataApi.getReportLabels({ dataset, field, site, search, all: true, limit: FRONTEND_MAX_OPTIONS });
          if (!active) return;
          const values = res.data?.list ?? [];
          setList(values);
          setTotal(values.length);
        } else if (!search && pageFields?.length) {
          const key = `${dataset}:${site}:${fieldSignature}`;
          let request = pageRequests.get(key);
          if (!request) { request = siteDataApi.getReportLabelPage({ dataset, site, fields: [...pageFields] }).then(res => res.data?.fields ?? {}); pageRequests.set(key, request); }
          const page = (await request)[field]; if (!active) return; setList(page?.list ?? []); setTotal(page?.total ?? 0);
        } else { const res = await siteDataApi.getReportLabels({ dataset, field, site, search }); if (!active) return; setList(res.data?.list ?? []); setTotal(res.data?.total ?? 0); }
      }
      finally { if (active) setLoading(false); }
    }, search ? 250 : 0);
    return () => { active = false; window.clearTimeout(timer); };
  }, [dataset, field, fieldSignature, pageFields, paginated, search, site]);
  const allOptions = list.length > 0 ? list : fallbackOptions;
  const sourceOptions = paginated ? allOptions.slice((page - 1) * pageSize, page * pageSize) : allOptions;
  const options: SelectOption[] = useMemo(() => {
    const values = includeAll ? ['全部', ...sourceOptions.filter(item => item !== '全部')] : [...sourceOptions];
    if (!paginated) value.forEach(item => { if (!values.includes(item)) values.push(item); });
    return values.map(item => ({ value: item, label: item, disabled: exclude && item === '全部' && !allowExcludeAll }));
  }, [allowExcludeAll, exclude, includeAll, sourceOptions, value]);
  const handleChange = (next: string[]) => {
    if (!includeAll || single) {
      onChange(next);
      return;
    }
    const selected = next.filter(item => item !== '全部');
    const hadAll = value.includes('全部');
    if (next.includes('全部') && !hadAll) {
      onChange(['全部']);
    } else if (selected.length === 0) {
      onChange(['全部']);
    } else {
      onChange(selected);
    }
  };
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const selectAll = () => handleChange([...new Set(allOptions)]);
  return <MultiSelect
    single={single}
    options={options}
    value={value}
    onChange={handleChange}
    placeholder="请选择"
    searchable={paginated || total > 50 || (list.length === 0 && fallbackOptions.length > 10)}
    onSearchChange={setSearch}
    clearable
    loading={loading}
    style={{ width }}
    exclude={exclude}
    onExcludeChange={onExcludeChange}
    allowExcludeAll={allowExcludeAll}
    onOpen={() => setPage(1)}
    onSelectAll={paginated ? selectAll : undefined}
    dropdownWidth={paginated ? 280 : undefined}
    dropdownFooter={paginated ? () => <Pagination className="report-label-pagination pagination-compact" page={page} totalPages={totalPages} onChange={setPage} disabled={loading} /> : undefined}
  />;
}
